// ProfileUpload.js
import React, { useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Modal, Button, Slider } from '@mui/material';
import Cropper from 'react-easy-crop';
import CloseIcon from '../../assets/images/close-icon.svg';
import ProfilePicture from '../../assets/images/user.svg';
import { useDispatch, useSelector } from "react-redux";
import {
  postDistributor,
  updateDistributor,
  setProfileDate,
  profilePictureView,
} from "../../store/reducers/auth";

import { toast } from "react-toastify";
const modalStyle = {
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 340,
  position: 'absolute',
  backgroundColor: '#fff',
  boxShadow: 24,
  padding: 32,
  boxSizing: 'border-box',
  borderRadius: 8,
};
export const convertToBase64 = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64Image = reader.result;
      resolve(base64Image);
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });
};
/* ───────── helper to draw the crop on a canvas ───────── */
const createImage = (url) =>
  new Promise((res, rej) => {
    const img = new Image();
    img.addEventListener('load', () => res(img));
    img.addEventListener('error', (err) => rej(err));
    img.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );
  return canvas.toDataURL('image/png');
}

/* ─────────────────────────────────────────────────────── */

const ProfileUpload = ({ onChange, image }) => {
  const [profileModal, setProfileModal] = useState(false); // first “Upload” dialog
  const [cropModal, setCropModal] = useState(false);       // cropper dialog

  const [rawSrc, setRawSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef(null);

  const dispatch = useDispatch();

  /* step 1: user chooses a file */
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);   // preview
    setRawSrc(url);
    setCropModal(true);
    setProfileModal(false);
  };

  /* react-easy-crop callback */
  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  /* step 2: save the cropped version */
  const handleSaveCrop = async () => {
    if (!rawSrc || !croppedAreaPixels) return;

    const base64 = await getCroppedImg(rawSrc, croppedAreaPixels);

    // Convert base64 to File
    const response = await fetch(base64);
    const blob = await response.blob();
    const file = new File([blob], 'cropped-image.png', { type: blob.type });

    
    
    URL.revokeObjectURL(rawSrc);
    
    setCrop({ x: 0, y: 0 });
    setZoom(1);

    const formData = new FormData();
    formData.append("image", file);
    setLoading(true);
    dispatch(profilePictureView(formData))
          .then((result) => {
            if (!result.error) {
              toast.success("Profile picture updated successfully!");
            }
            setCropModal(false);
            setLoading(false);
            setRawSrc(null);
            onChange(file, base64,result); // pass File and base64
          })
          .catch((error) => {
            console.error("Profile picture upload failed:", error);
            toast.error("Failed to update profile picture");
            setLoading(false);
            setCropModal(false);
            setRawSrc(null);
    });
  };

  return (
    <>
      {/* thumbnail or placeholder */}
      <div className="input-group">
        {image ? (
          <img
            src={image}
            alt="avatar"
            className="profile-picture"
            style={{ cursor: 'pointer' }}
            onClick={() => setProfileModal(true)}
          />
        ) : (
          <div
            className="profile-picture"
            style={{ cursor: 'pointer' }}
            onClick={() => setProfileModal(true)}
          >
            <div className="profile-pic-placeholder">
              <img src={ProfilePicture} alt="placeholder" />
              <span>Upload Logo</span>
            </div>
          </div>
        )}
      </div>

      {/* hidden file input */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* ── 1st modal: pick file ── */}
      <Modal open={profileModal} onClose={() => setProfileModal(false)}>
        <div style={modalStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <h2>Upload Logo</h2>
            <img
              src={CloseIcon}
              alt="close"
              style={{ cursor: 'pointer' }}
              onClick={() => setProfileModal(false)}
            />
          </div>
          <div className="input-group button">
          <Button
            fullWidth
            type="button"
              variant="standard"
              color="primary"
            
            onClick={() => fileInputRef.current.click()}
          >
            Choose File
          </Button>
          </div>
        </div>
      </Modal>

      {/* ── 2nd modal: cropper ── */}
      <Modal open={cropModal} onClose={() => setCropModal(false)}>
        <div style={{ ...modalStyle, width: 380 }}>
          <h3 style={{ marginBottom: 16 }}>Crop Image</h3>

          {rawSrc && (
            <>
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  height: 280,
                  background: '#333',
                }}
              >
                <Cropper
                  image={rawSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>

              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(_, z) => setZoom(z)}
                sx={{ mt: 2 }}
              />

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
                <Button variant="text" onClick={() => setCropModal(false)}>
                  Cancel
                </Button>
                <Button loading={loading} variant="contained" onClick={handleSaveCrop}>
                  Save
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ProfileUpload;
